import { camelCase } from 'lodash-es'
import { ComboboxItem } from '@node-space/ui-components'
import { CustomerDocumentStatus, CustomerStatus } from 'types/customers'
import { titleCase } from 'utils/utils'
import { getTranslation } from '../../../../__mocks__/i18nextMock'
import { BusinessCustomerFormKey, IndividualCustomerFormData, RequiredDocument } from './types'

export const EMBEDDED_CUSTOMER_STATUSES = {
  PENDING_AGREEMENTS: 'PENDING_AGREEMENTS',
  INFO_REQUIRED: 'INFO_REQUIRED',
  ...CustomerStatus,
  TERMINATED: 'TERMINATED',
} as const

export const EMBEDDED_CUSTOMER_AGREEMENT_STATUSES = {
  PENDING: 'PENDING',
  SIGNED: 'SIGNED',
  DECLINED: 'DECLINED',
} as const

export const RISK_SCORES = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
} as const

export const USE_CASES = {
  STABLECOIN_PAYOUTS: 'STABLECOIN_PAYOUTS',
  EMBEDDED_STABLECOIN_WALLETS: 'EMBEDDED_STABLECOIN_WALLETS',
  EMBEDDED_FIAT_ACCOUNTS: 'EMBEDDED_FIAT_ACCOUNTS',
} as const

export const PAGE_SIZE = 10

export const COMPANY_REQUIRED_DOCUMENTS: RequiredDocument[] = [
  { subType: 'DIRECTORS_REGISTRY', nameKey: 'directorsRegistry' },
  {
    subType: 'INCORPORATION_CERT',
    nameKey: 'incorporationCertificate',
  },
  {
    subType: 'INCORPORATION_ARTICLES',
    nameKey: 'incorporationArticles',
  },
  { subType: 'SHAREHOLDER_REGISTRY', nameKey: 'shareholdersRegistry' },
  { subType: 'PROOF_OF_ADDRESS', nameKey: 'proofOfAddress' },
  { type: 'OTHER', nameKey: 'other' },
]

export const INDIVIDUAL_REQUIRED_DOCUMENTS: RequiredDocument[] = [
  {
    nameKey: 'proofOfAddress',
    type: ['PROOF_OF_ADDRESS', 'UTILITY_BILL', 'PROOF_OF_RESIDENCE'],
  },
  {
    nameKey: 'idCard',
    type: ['PASSPORT', 'ID_CARD', 'DRIVER_LICENSE', 'RESIDENCE_PERMIT'],
  },
  { nameKey: 'selfie', type: 'SELFIE' },
]

export const APPROVED_DOCUMENT_STATUSES = [
  CustomerDocumentStatus.APPROVED,
  CustomerDocumentStatus.VERIFIED,
] as const

export const DECLINED_DOCUMENT_STATUSES = [
  CustomerDocumentStatus.DECLINE,
  CustomerDocumentStatus.REJECTED,
] as const

export const INDIVIDUAL_CUSTOMER_ERROR_MAPPING: Record<string, keyof IndividualCustomerFormData> = {
  'individual.firstName': 'firstName',
  'individual.lastName': 'lastName',
  'individual.dateOfBirth': 'dateOfBirth',
  'individual.emailAddress': 'emailAddress',
  'individual.description': 'description',
  'individual.nationality': 'nationality',
  'individual.address.addressLine1': 'addressLine1',
  'individual.address.addressLine2': 'addressLine2',
  'individual.address.city': 'city',
  'individual.address.postalCode': 'postalCode',
  'individual.person.address.postalCode': 'postalCode',
  'individual.address.countryCode': 'countryCode',
  'individual.address.stateCode': 'stateCode',
  'individual.person.taxIdentification.number': 'taxIdentificationNumber',
  'individual.taxIdentification.number': 'taxIdentificationNumber',
  'individual.taxIdentification.taxResidenceCountryCode': 'taxResidenceCountryCode',
  'individual.cdd.expectedMonthlyVolume.amount': 'expectedMonthlyVolumeAmount',
  'individual.cdd.expectedMonthlyVolume.currency': 'expectedMonthlyVolumeCurrency',
  'individual.cdd.employmentStatus': 'employmentStatus',
  'individual.cdd.sourceOfFunds': 'sourceOfFunds',
  'individual.cdd.pepStatus': 'pepStatus',
  'individual.cdd.intendedUseOfAccount': 'intendedUseOfAccount',
  useCase: 'useCase',
  externalReference: 'externalReference',
}

export const BUSINESS_CUSTOMER_ERROR_MAPPING: Record<string, BusinessCustomerFormKey> = {
  'company.name': 'businessName',
  'company.description': 'businessDescription',
  'company.industryReference': 'industry',
  'company.address.countryCode': 'countryOfIncorporation',
  'company.monthlyExpectedVolumesReference': 'monthlyExpectedVolumes',
  useCase: 'useCase',
  externalReference: 'externalReference',
}

export const URGENCY_THRESHOLD_OPTIONS = [
  { leftText: '1 day', value: '1' },
  { leftText: '2 days', value: '2' },
  { leftText: '3 days', value: '3' },
  { leftText: '4 days', value: '4' },
  { leftText: '5 days', value: '5' },
]

export const RADIO_OPTIONS = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
]

export const ESTIMATED_YEARLY_INCOME_VALUES = [
  'INCOME_0_TO_50K',
  'INCOME_50K_TO_100K',
  'INCOME_100K_TO_250K',
  'INCOME_250K_TO_500K',
  'INCOME_500K_TO_750K',
  'INCOME_750K_TO_1M',
  'INCOME_ABOVE_1M',
]

export const ESTIMATED_YEARLY_INCOME_OPTIONS: ComboboxItem[] = ESTIMATED_YEARLY_INCOME_VALUES.map(
  value => ({
    value,
    leftText: titleCase(value.replace('INCOME_', '')),
  })
)

export const EMPLOYMENT_INDUSTRY_SECTOR_VALUES: string[] = [
  'ACCOMMODATION_FOOD_SERVICES',
  'ADMINISTRATIVE_SUPPORT_WASTE_MANAGEMENT_REMEDIATION_SERVICES',
  'ADULT_ENTERTAINMENT',
  'AGRICULTURE_FORESTRY_FISHING_HUNTING',
  'ARTS_ENTERTAINMENT_RECREATION',
  'AUCTIONS',
  'AUTOMOBILES',
  'BLOCKCHAIN',
  'CONSTRUCTION',
  'CRYPTO',
  'DRUGS',
  'E_COMMERCE',
  'EDUCATIONAL_SERVICES',
  'EXPORT_IMPORT',
  'FINANCE_INSURANCE',
  'FINANCIAL_INSTITUTION',
  'GAMBLING',
  'HEDGE_FUND',
  'HEALTH_CARE_SOCIAL_ASSISTANCE',
  'INFORMATION',
  'INSURANCE',
  'INVESTMENT',
  'MANAGEMENT_OF_COMPANIES_ENTERPRISES',
  'MANUFACTURING',
  'MARKET_MAKER',
  'MINING',
  'MONEY_SERVICE_BUSINESS',
  'NON_PROFIT',
  'OTHER_SERVICES',
  'PRECIOUS_METALS',
  'PROFESSIONAL_SCIENTIFIC_TECHNICAL_SERVICES',
  'PUBLIC_ADMINISTRATION',
  'REAL_ESTATE_RENTAL_LEASING',
  'REGISTERED_INVESTMENT_ADVISOR',
  'RETAIL_TRADE',
  'SHELL_BANK',
  'STO_ISSUER',
  'TRANSPORTATION_WAREHOUSING',
  'TRAVEL_TRANSPORT',
  'UTILITIES',
  'WEAPONS',
  'WHOLESALE_TRADE',
  'NOT_CLASSIFIED',
]

export const EMPLOYMENT_INDUSTRY_SECTOR_OPTIONS: ComboboxItem[] =
  EMPLOYMENT_INDUSTRY_SECTOR_VALUES.map(value => ({
    value,
    leftText: getTranslation(`customers.employmentIndustrySectorOptions.${camelCase(value)}`),
  }))
